import React, { ReactElement } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { FieldErrors, useFieldArray, useFormContext } from "react-hook-form";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

interface DangerousGoodsFormProps {
  classes: {
    titlesBoxStyle: string;
    typography: string;
    textAndDropdownGrids: string;
    textField: string;
    formControl: string;
    inputLabelAndMenuItems: string;
  };
}

const DangerousGoodsForm: React.FC<DangerousGoodsFormProps> = ({
  classes,
}: DangerousGoodsFormProps): ReactElement => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const { fields } = useFieldArray({
    name: "hazardousUpdateRequestDtos",
  });
  if (fields.length === 0) return <></>;

  const hazardousErrors = errors.hazardousUpdateRequestDtos as
    | FieldErrors[]
    | undefined;

  const errorBorder = hazardousErrors
    ? {
        border: "1px solid red",
        boxShadow: "0 0 1px red",
      }
    : {};
  return (
    <>
      <Box className={classes.titlesBoxStyle}>
        <Typography className={classes.typography} variant="h6">
          Dangerous Goods
        </Typography>
      </Box>
      <Accordion
        style={{ marginBottom: "15px" }}
        sx={errorBorder}
        defaultExpanded
      >
        <AccordionSummary
          expandIcon={<ArrowDownwardIcon />}
          aria-controls="dangerousGoods-content"
          id="dangerousGoods-header"
        >
          <Typography>Hazardous</Typography>
        </AccordionSummary>

        <AccordionDetails>
          {fields.map((item, index) => {
            return (
              <Accordion
                key={item.id}
                sx={hazardousErrors?.[index] ? errorBorder : {}}
                defaultExpanded={hazardousErrors?.[index] ? true : false}
              >
                <AccordionSummary
                  expandIcon={<ArrowDropDownIcon />}
                  aria-controls={"hazardous" + index + "-content"}
                  id={"hazardous" + index + "-header"}
                >
                  <Typography>{"Hazardous " + (index + 1)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid
                    container
                    spacing={2}
                    className={classes.textAndDropdownGrids}
                  >
                    <Grid item xs={3}>
                      <TextField
                        className={classes.textField}
                        id={`hazardousUpdateRequestDtos.${index}.unNumber`}
                        type="text"
                        label="UN Number"
                        {...register(
                          `hazardousUpdateRequestDtos.${index}.unNumber`
                        )}
                        error={Boolean(hazardousErrors?.[index]?.unNumber)}
                        helperText={
                          hazardousErrors?.[
                            index
                          ]?.unNumber?.message?.toString() || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        className={classes.textField}
                        id={`hazardousUpdateRequestDtos.${index}.adrClass`}
                        type="text"
                        label="ADR Class"
                        {...register(
                          `hazardousUpdateRequestDtos.${index}.adrClass`
                        )}
                        error={Boolean(hazardousErrors?.[index]?.adrClass)}
                        helperText={
                          hazardousErrors?.[
                            index
                          ]?.adrClass?.message?.toString() || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        type="text"
                        className={classes.textField}
                        id={`hazardousUpdateRequestDtos.${index}.adrPackingGroup`}
                        label="ADR Packing Group"
                        {...register(
                          `hazardousUpdateRequestDtos.${index}.adrPackingGroup`
                        )}
                        error={Boolean(
                          hazardousErrors?.[index]?.adrPackingGroup
                        )}
                        helperText={
                          hazardousErrors?.[
                            index
                          ]?.adrPackingGroup?.message?.toString() || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        type="number"
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        className={classes.textField}
                        id={`hazardousUpdateRequestDtos.${index}.adrNumberOfPackages`}
                        label="ADR Number of Packages"
                        {...register(
                          `hazardousUpdateRequestDtos.${index}.adrNumberOfPackages`,
                          {
                            valueAsNumber: true,
                          }
                        )}
                        error={Boolean(
                          hazardousErrors?.[index]?.adrNumberOfPackages
                        )}
                        helperText={
                          hazardousErrors?.[
                            index
                          ]?.adrNumberOfPackages?.message?.toString() || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        type="number"
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        className={classes.textField}
                        id={`hazardousUpdateRequestDtos.${index}.adrNetWeight`}
                        label="ADR Net Weight"
                        {...register(
                          `hazardousUpdateRequestDtos.${index}.adrNetWeight`,
                          {
                            valueAsNumber: true,
                          }
                        )}
                        error={Boolean(hazardousErrors?.[index]?.adrNetWeight)}
                        helperText={
                          hazardousErrors?.[
                            index
                          ]?.adrNetWeight?.message?.toString() || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        type="number"
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        className={classes.textField}
                        id={`hazardousUpdateRequestDtos.${index}.adrGrossWeight`}
                        label="ADR Gross Weight"
                        {...register(
                          `hazardousUpdateRequestDtos.${index}.adrGrossWeight`,
                          {
                            valueAsNumber: true,
                          }
                        )}
                        error={Boolean(
                          hazardousErrors?.[index]?.adrGrossWeight
                        )}
                        helperText={
                          hazardousErrors?.[
                            index
                          ]?.adrGrossWeight?.message?.toString() || ""
                        }
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default DangerousGoodsForm;
